import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { hasPendingConsent, clearPendingConsent } from '../consent';
import { recordGdprConsent } from '../api';
import { supabase } from '@/shared/lib/supabase';

/**
 * Estado de sesión de Supabase Auth con actualización reactiva.
 * Se suscribe a onAuthStateChange y cancela al desmontar.
 */
export function useSession(): {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
} {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setIsLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      setIsLoading(false);

      // §12: si venimos de un alta con el consentimiento aceptado, registrarlo.
      // Es el primer momento en que hay sesión a la que colgarlo; el «acepto»
      // se dio antes de irse a Google o Microsoft, y no sobrevive al viaje en
      // memoria (`../consent`).
      //
      // **La marca se borra sólo si el servidor lo aceptó.** Antes se borraba
      // en el `.then` de `supabase.rpc`, que resuelve igual cuando la llamada
      // falla —`supabase-js` no rechaza, devuelve `{ error }`—, así que un
      // rechazo se llevaba por delante el consentimiento pendiente y no había
      // segunda oportunidad. Con la función rota de #57 eso pasaba **siempre**.
      if (event === 'SIGNED_IN' && newSession && hasPendingConsent()) {
        void recordGdprConsent()
          .then(clearPendingConsent)
          .catch(() => {
            // Se queda pendiente a propósito: el `ConsentGate` lo pedirá al
            // entrar, que es el camino que ya cubre a quien nunca lo dio.
          });
      }
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return { session, user: session?.user ?? null, isLoading };
}
